// ── RMA'S COVERAGE LADDER, AND WHAT A QUOTE DEFAULTS TO ──────────────────────
//
// Kevin, on a farm carrying 60%: *"should they go to 65% 75% etc, or should they
// stay at 60%  — right now our recommendation engine is not smart enough i dont
// think"*.  It was not: `CANDIDATE_LEVELS` was the literal `[0.75, 0.80, 0.85]`,
// so a 60% farm's own election was not in the set and neither was the step it
// would actually consider.  The engine was structurally unable to answer.
//
// **THE ENGINE COULD ALWAYS PRICE THE WHOLE LADDER — ONLY THE CANDIDATE SET WAS
// NARROW.**  Measured 2026-09-22 by driving the shipped `quoteWheatRP2027` over
// every one of the **1,104** (state x county x practice) cells in the CY2027
// footprint: all 1,104 return a quote at all eight buy-up levels, 0.50 through
// 0.85.  So widening the set adds no lookup that can miss.
//
// ── WHY "INCUMBENT ±1" AND NOT "THE WHOLE LADDER" ────────────────────────────
//
// The subsidy schedule makes the ladder wildly non-linear, and the steps a
// grower would actually consider are the neighbouring ones.  NY GENESEE, APH 77,
// 200 ac, EU, $7.54 — net producer premium per acre, and the cost of each step:
//
//     0.50  $2.85        0.70  $6.28   +1.11
//     0.55  $3.43 +0.58  0.75  $7.55   +1.27
//     0.60  $4.24 +0.81  0.80 $13.04   +5.48   <- the cliff
//     0.65  $5.17 +0.93  0.85 $23.41  +10.38
//
// A farm at 60% is looking at a 93-cent step.  A farm at 75% is looking at
// $5.48.  Those are not the same conversation, and a fixed [75, 80, 85] set
// showed the second one to both of them.
//
// **The quote defaults to the neighbours; the optimizer prices the whole
// ladder.**  Two different questions — "what should this renewal show" and
// "what would every level have done" — and conflating them is what produced a
// three-level set in the first place.
//
// ⚠ **CAT IS NOT THE BOTTOM OF THIS LADDER.**  It is a different product — YP at
// a 0.55 price election, not RP buy-up — which this repo already records
// (`isCatIncumbent`, and the ten wheat units at coverage 0.50 with
// `mpci_plan_code = "01"` sitting beside twenty-five genuine 0.50 RP buy-ups).
// So CAT gets its own rung BELOW 0.50 and a step off it is a PRODUCT change, not
// a level change.  Folding CAT in as "0.50" would quietly recommend a product
// switch as though it were a notch.

// RMA's buy-up levels for wheat, lowest first.  Verified offered in all 1,104
// cells of the CY2027 footprint rather than taken from a handbook.
export const WHEAT_LEVELS = [0.50, 0.55, 0.60, 0.65, 0.70, 0.75, 0.80, 0.85];
export const LEVEL_STEP = 0.05;

// A prospect has no incumbent to step from, so the benchmark anchors the set.
// This reproduces the pre-2026-09-22 behaviour exactly for new business.
export const ANCHOR_LEVEL = 0.80;
export const PROSPECT_LEVELS = [0.75, 0.80, 0.85];

// The APH rule this repo proved against the AIP's own rate_yield.  Ten years is
// where the reconstruction is EXACT (74 of 74); four is RMA's own minimum and
// the floor below which a database is filled with transitional yields we do not
// hold.  Both live here so the replay and the ladder cannot disagree about them.
export const WHEAT_APH_MAX_YEARS = 10;
export const WHEAT_APH_MIN_YEARS = 4;

export const CAT_RUNG = "CAT";

const r2 = (n) => Math.round(n * 100) / 100;

// Index on the ladder.  CAT is -1; anything off the ladder is null rather than
// snapped to a neighbour — a level RMA does not sell is a finding, not a typo.
export function rungOf(level) {
  if (level === CAT_RUNG) return -1;
  const v = r2(Number(level));
  const i = WHEAT_LEVELS.findIndex((L) => r2(L) === v);
  return i < 0 ? null : i;
}

// ⚠ CAT is decided by the PLAN, never by the coverage level.  A 0.50 RP buy-up
// and a 0.50 CAT policy carry the same number in `coverage_level` and are
// different products; measured on the live book, 10 wheat units are CAT and 25
// are genuine 0.50 buy-up.
export function isCat(inc) {
  if (!inc) return false;
  if (inc.level === CAT_RUNG || inc.cat === true) return true;
  const plan = String(inc.planCode || inc.mpci_plan_code || "").trim();
  if (plan === "01" && Number(inc.level) <= 0.5) return true;
  // A price election at 55% is CAT's signature where the plan code is absent.
  const pe = Number(inc.priceElectionPct);
  return !!(pe > 0 && pe <= 0.56 && Number(inc.level) <= 0.5);
}

// The levels a RENEWAL quote shows: the incumbent and its neighbours.
//
// Returns { levels, incumbentLevel, incumbentIsCat, capped } — `capped` names the
// end of the ladder it ran into, so a card can say "85% is the top RMA sells"
// rather than silently showing two options where others get three.
export function candidateLevels(incumbent, opts = {}) {
  const span = opts.span == null ? 1 : Math.max(0, Math.round(opts.span));
  if (!incumbent || (incumbent.level == null && !isCat(incumbent))) {
    return { levels: PROSPECT_LEVELS.slice(), incumbentLevel: null, incumbentIsCat: false,
             capped: null, basis: "prospect" };
  }
  if (isCat(incumbent)) {
    // ⚠ CAT IS PRICED AS CAT, AND THE LEVELS HERE ARE THE BUY-UPS BESIDE IT.
    // `levels` is a pricing input, so it carries NUMBERS only — handing the caller
    // the string "CAT" in a list it is about to quote would either throw or, worse,
    // coerce to NaN and drop a rung silently.  `incumbentIsCat` is how the caller
    // knows the incumbent is a different product and must be priced with `cat: true`.
    const levels = WHEAT_LEVELS.slice(0, 1 + span);
    return { levels, incumbentLevel: 0.50, incumbentIsCat: true, capped: "bottom", basis: "cat" };
  }
  const i = rungOf(incumbent.level);
  if (i == null) {
    // An election off RMA's ladder — report it rather than snapping it.
    return { levels: PROSPECT_LEVELS.slice(), incumbentLevel: r2(Number(incumbent.level)),
             incumbentIsCat: false, capped: null, basis: "off_ladder" };
  }
  const lo = Math.max(0, i - span), hi = Math.min(WHEAT_LEVELS.length - 1, i + span);
  return {
    levels: WHEAT_LEVELS.slice(lo, hi + 1),
    incumbentLevel: WHEAT_LEVELS[i],
    incumbentIsCat: false,
    capped: hi === WHEAT_LEVELS.length - 1 && i + span > hi ? "top"
          : lo === 0 && i - span < lo ? "bottom" : null,
    basis: "incumbent",
  };
}

// Whole-ladder set for the optimizer.  Deliberately a SEPARATE function from
// candidateLevels: one decides what a quote defaults to, the other decides what
// a replay prices, and a single function serving both is how the quote came to
// show three fixed levels to everybody.
export function allLevels() { return WHEAT_LEVELS.slice(); }

export function levelLabel(level) {
  return level === CAT_RUNG ? "CAT" : `${Math.round(Number(level) * 100)}%`;
}
